import { MRT_ColumnDef } from "material-react-table"
import { TableCell } from "./Atom"
import { primaryTabs, secondayTabs } from "../../../pages/ResourceBudget/types"
import { strFormat } from "./utils"
import { colors } from "../../../../styles/commonStyles"

// Function to add dirty cell styling to columns
export const addDirtyCellStyling = (
  columns: MRT_ColumnDef<any>[],
  dirtyCells: string[],
  isNewEntryRow: boolean,
) => {
  return columns.map((col) => ({
    ...col,
    muiTableBodyCellProps: ({ cell, row }: any) => {
      // Adjust for the "+ Add" button row at index 0
      // The cell.id format is "rowIndex_fieldName"
      // We need to adjust the rowIndex to account for the "+ Add" button
      const cellIdParts = cell.id.split("_")
      const rowIndex = parseInt(cellIdParts[0])
      const fieldName = cellIdParts.slice(1).join("_")

      // If this is the "+ Add" button row (index 0), don't apply dirty styling
      // Also skip dirty styling for the newly added row at index 1 when a new entry exists
      if (rowIndex === 0 || (isNewEntryRow && rowIndex === 1)) {
        return {
          ...col.muiTableBodyCellProps,
          sx: {
            ...(col.muiTableBodyCellProps as any)?.sx,
            borderColor: "#E4E4E4",
            borderWidth: "0.5px",
          },
        }
      }

      // Adjust the row index to match the data array (remove "+ Add" button offset)
      const adjustedRowIndex = rowIndex - 1
      const adjustedCellId = `${adjustedRowIndex}_${fieldName}`

      return {
        ...col.muiTableBodyCellProps,
        sx: {
          ...(col.muiTableBodyCellProps as any)?.sx,
          borderColor: dirtyCells.includes(adjustedCellId)
            ? colors.primaryRed
            : "#E4E4E4",
          borderWidth: dirtyCells.includes(adjustedCellId) ? "2px" : "0.5px",
        },
      }
    },
  }))
}

// Utilities
export const StringToCurrencyConverter = ({ renderedCellValue }: any) => {
  if (renderedCellValue) {
    const val = parseFloat(renderedCellValue).toFixed(2)
    return `$ ${val?.toLocaleString()}`
  }
  return renderedCellValue
}
export const floatToInt = ({ renderedCellValue }: any) =>
  renderedCellValue
    ? parseFloat(renderedCellValue).toFixed(2)
    : renderedCellValue

export const convertToString = ({ renderedCellValue }: any) => {
  return renderedCellValue
}

const disableColStyles = {
  sx: {
    backgroundColor: colors.grey50,
  },
}
// Common columns configuration
const commonColumns: MRT_ColumnDef<any>[] = [
  {
    header: "No. of Days",
    accessorKey: "no_of_days",
    maxSize: 100,
    enableEditing: true, // Allow editing for all rows
    Cell: ({ renderedCellValue, row }) => (
      <TableCell renderValue={renderedCellValue as string} formatType="*" />
    ),
  },
  {
    header: "Hours/Month",
    accessorKey: "hours_per_month",
    enableEditing: false,
    maxSize: 100,
    muiTableBodyCellProps: disableColStyles,
    Cell: ({ renderedCellValue, row }) => (
      <TableCell renderValue={renderedCellValue as string} formatType="*" />
    ),
  },
  {
    header: "Production",
    accessorKey: "production",
    enableEditing: false,
    maxSize: 100,
    muiTableBodyCellProps: disableColStyles,
    Cell: ({ renderedCellValue, row }) => (
      <TableCell renderValue={renderedCellValue as string} formatType="$" />
    ),
  },
  {
    header: "Compensation %",
    accessorKey: "compensation_percent",
    maxSize: 100,
    enableEditing: true, // Allow editing for all rows
    Cell: ({ renderedCellValue, row }) => {
      return (
        <TableCell
          renderValue={
            renderedCellValue !== null ? `${renderedCellValue} %` : ""
          }
        />
      )
    },
  },
  {
    header: "Compensation $",
    accessorKey: "compensation_amount",
    maxSize: 100,
    enableEditing: true, // Allow editing for all rows
    Cell: ({ renderedCellValue, row }) => (
      <TableCell renderValue={renderedCellValue as string} formatType="$" />
    ),
  },
  {
    header: "Wages/HR",
    accessorKey: "wages_per_hour",
    maxSize: 100,
    enableEditing: true, // Allow editing for all rows
    Cell: ({ renderedCellValue, row }) => (
      <TableCell renderValue={renderedCellValue as string} formatType="$" />
    ),
  },
]

// Doctors specific configuration
const doctorsConfig: MRT_ColumnDef<any>[] = [
  {
    header: "Hours/Day",
    accessorKey: "hours_per_day",
    maxSize: 100,
    enableEditing: true, // Allow editing for all rows
    Cell: ({ renderedCellValue, row }) => (
      <TableCell renderValue={renderedCellValue as string} formatType="*" />
    ),
  },
  {
    header: "Production per Hour",
    accessorKey: "amount_per_hour",
    maxSize: 100,
    enableEditing: true, // Allow editing for all rows
    Cell: ({ renderedCellValue, row }) => (
      <TableCell renderValue={renderedCellValue as string} formatType="$" />
    ),
  },
  {
    header: "Wages Total",
    accessorKey: "wages_per_month",
    maxSize: 100,
    enableEditing: true, // Allow editing for all rows
    Cell: ({ renderedCellValue, row }) => (
      <TableCell renderValue={renderedCellValue as string} formatType="$" />
    ),
  },
]

// Hygienists specific configuration
const hygienistsConfig: MRT_ColumnDef<any>[] = [
  {
    header: "Hours/Day",
    accessorKey: "hours_per_day",
    maxSize: 100,
    enableEditing: true, // Allow editing for all rows
    Cell: ({ renderedCellValue, row }) => (
      <TableCell renderValue={renderedCellValue as string} formatType="*" />
    ),
  },
  {
    header: "Production per Hour",
    accessorKey: "amount_per_hour",
    maxSize: 100,
    enableEditing: true, // Allow editing for all rows
    Cell: ({ renderedCellValue, row }) => (
      <TableCell renderValue={renderedCellValue as string} formatType="$" />
    ),
  },
  {
    header: "Wages Total",
    accessorKey: "wages_per_month",
    maxSize: 100,
    enableEditing: true, // Allow editing for all rows
    Header: () => (
      <TableCell
        renderValue="Wages Total"
        subtitle="total hours x hourly rate"
      />
    ),
    Cell: ({ renderedCellValue, row }) => (
      <TableCell renderValue={renderedCellValue as string} formatType="$" />
    ),
  },
]

// Complete configurations for each tab
const doctorsTableConfig: MRT_ColumnDef<any>[] = [
  ...commonColumns.slice(0, 1), // No. of Days
  ...doctorsConfig.slice(0, 1), // Hours/Day
  ...commonColumns.slice(1, 2), // Hours/Month
  ...doctorsConfig.slice(1, 2), // Production per Hour
  ...commonColumns.slice(2, 7), // Production, Compensation %, Compensation $, Wages/HR
  ...doctorsConfig.slice(2, 3), // Wages Total
]

const hygienistsTableConfig: MRT_ColumnDef<any>[] = [
  ...commonColumns.slice(0, 1), // No. of Days
  ...hygienistsConfig.slice(0, 1), // Chairside Hours/Day
  ...commonColumns.slice(1, 2), // Hours/Month
  ...hygienistsConfig.slice(1, 2), // Production per Hour
  ...commonColumns.slice(2, 7), // Production, Compensation %, Compensation $, Wages/HR
  ...hygienistsConfig.slice(2, 3), // Wages Total with subtitle
]
const NCConfig: MRT_ColumnDef<any>[] = [
  {
    header: "Non Clinical Name",
    accessorKey: "resource_name",
    enableEditing: true,
    size: 220,
    Cell: ({ renderedCellValue, row }) => (
      <TableCell
        col="resource_name"
        renderValue={renderedCellValue as string}
      />
    ),
  },
  {
    header: "Role Category",
    accessorKey: "role_category",
    enableEditing: true, // Allow editing for all rows
    Cell: ({ renderedCellValue, row }) => (
      <TableCell renderValue={strFormat(renderedCellValue as string)} />
    ),
  },
  {
    header: "Role Title",
    accessorKey: "role_title",
    Cell: ({ renderedCellValue, row }) => (
      <TableCell renderValue={renderedCellValue as string} />
    ),
  },
  {
    header: "No. of Days",
    accessorKey: "no_of_days",
    maxSize: 100,
    enableEditing: true, // Allow editing for all rows
    Cell: ({ renderedCellValue, row }) => (
      <TableCell renderValue={renderedCellValue as string} formatType="*" />
    ),
  },
  {
    header: "Hours/Day",
    accessorKey: "hours_per_day",
    maxSize: 100,
    enableEditing: true, // Allow editing for all rows
    Cell: ({ renderedCellValue, row }) => (
      <TableCell renderValue={renderedCellValue as string} formatType="*" />
    ),
  },
  {
    header: "Hours/Month",
    accessorKey: "hours_per_month",
    enableEditing: false,
    maxSize: 100,
    muiTableBodyCellProps: disableColStyles,
    Cell: ({ renderedCellValue, row }) => (
      <TableCell renderValue={renderedCellValue as string} formatType="*" />
    ),
  },
  {
    header: "Wages/HR",
    accessorKey: "wages_per_hour",
    maxSize: 100,
    enableEditing: true, // Allow editing for all rows
    Cell: ({ renderedCellValue, row }) => (
      <TableCell renderValue={renderedCellValue as string} formatType="$" />
    ),
  },
  {
    header: "Wages Total",
    accessorKey: "wages_per_month",
    maxSize: 100,
    enableEditing: true, // Allow editing for all rows
    Cell: ({ renderedCellValue, row }) => (
      <TableCell renderValue={renderedCellValue as string} formatType="$" />
    ),
  },
]
const CDConfig: MRT_ColumnDef<any>[] = [
  {
    header: "Doctors Name",
    accessorKey: "resource_name",
    enableEditing: (row) => {
      // Only allow editing for new entries that don't have an ID yet
      // If the row has an ID, it means it has been successfully created and should not be editable
      return row.index === 1 && !row.original?.id
    },
    size: 220,
    Cell: ({ renderedCellValue, row }) => (
      <TableCell
        col="resource_name"
        row={row}
        renderValue={renderedCellValue as string}
      />
    ),
  },
  ...doctorsTableConfig,
]
const CHConfig: MRT_ColumnDef<any>[] = [
  {
    header: "Hygienists Name",
    accessorKey: "resource_name",
    size: 220,
    Cell: ({ renderedCellValue, row }) => (
      <TableCell
        col="resource_name"
        row={row}
        renderValue={renderedCellValue as string}
      />
    ),
    enableEditing: (row) => {
      // Only allow editing for new entries that don't have an ID yet
      // If the row has an ID, it means it has been successfully created and should not be editable
      return row.index === 1 && !row.original?.id
    },
  },
  ...hygienistsTableConfig,
]

// exporting columns
export const columns: any = {
  [primaryTabs.nonClinical]: {
    pinningCol: "resource_name",
    additionalPinningCols: "role_category",
    cols: NCConfig,
  },
  [secondayTabs.doctors]: {
    pinningCol: "resource_name",
    cols: CDConfig,
  },
  [secondayTabs.hygienists]: {
    pinningCol: "resource_name",
    cols: CHConfig,
  },
}

export const columnHeader = [
  "no_of_days",
  "hours_per_day",
  "hours_per_month",
  "amount_per_hour",
  "production",
  "compensation_amount",
  "compensation_percent",
  "wages_per_hour",
  "wages_per_month",
]

export const clinicalTableFooter = {
  no_of_days: 0.0,
  hours_per_day: 0.0,
  hours_per_month: 0.0,
  wages_per_hour: 0.0,
  wages_per_month: 0.0,
  amount_per_hour: 0.0,
  production: 0.0,
  compensation_amount: 0.0,
  compensation_percent: 0.0,
}
export const nonClinicalTableFooter = {
  no_of_days: 0.0,
  hours_per_day: 0.0,
  hours_per_month: 0.0,
  wages_per_hour: 0.0,
  wages_per_month: 0.0,
}
